import {
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const MEMORY_SIZE = 0x0410
const RADIO_MEMORY_SIZE = 0x0400
const FILE_HEADER_SIZE = 0x10
const CHANNEL_START = 0x0030
const CHANNEL_SIZE = 16
const CHANNEL_COUNT = 8
const BLOCK_SIZE = 8

type TKx102Spec = {
  id: string
  model: string
  ident: string
  summary: string
}

export type TKx102Profile = {
  protocol: 'kenwood-tkx102'
  id: string
  vendor: 'Kenwood'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  parity: 'even'
  channelMinimum: 1
  channelMaximum: 8
  nameLength: 0
  memorySize: number
  ident: string
  powerLevels: readonly ['Low', 'High']
}

const SPECS: readonly TKx102Spec[] = [
  {
    id: 'kenwood-tk-7102',
    model: 'TK-7102',
    ident: '7102',
    summary: 'Kenwood TK-7102 VHF mobile with eight memory channels.',
  },
  {
    id: 'kenwood-tk-8102',
    model: 'TK-8102',
    ident: '8102',
    summary: 'Kenwood TK-8102 UHF mobile with eight memory channels.',
  },
  {
    id: 'kenwood-tk-7108',
    model: 'TK-7108',
    ident: '7108',
    summary: 'Kenwood TK-7108 VHF mobile with eight memory channels.',
  },
  {
    id: 'kenwood-tk-8108',
    model: 'TK-8108',
    ident: '8108',
    summary: 'Kenwood TK-8108 UHF mobile with eight memory channels.',
  },
]

export const TKX102_PROFILES: readonly TKx102Profile[] = SPECS.map((spec) => ({
  protocol: 'kenwood-tkx102',
  id: spec.id,
  vendor: 'Kenwood',
  model: spec.model,
  summary: spec.summary,
  tags: ['Experimental', 'Read + write', '8 channels', 'No channel names'],
  baudRate: 9600,
  parity: 'even',
  channelMinimum: 1,
  channelMaximum: CHANNEL_COUNT,
  nameLength: 0,
  memorySize: MEMORY_SIZE,
  ident: spec.ident,
  powerLevels: ['Low', 'High'],
}))

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'Low',
    skip: false,
  }
}

function readUint16LE(raw: Uint8Array, offset: number): number {
  return raw[offset] | (raw[offset + 1] << 8)
}

function writeUint16LE(raw: Uint8Array, offset: number, value: number): void {
  raw[offset] = value & 0xff
  raw[offset + 1] = (value >> 8) & 0xff
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(value: number): DecodedTone {
  if (value === 0 || value === 0xffff) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if ((value & 0x2800) === 0x2800) {
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: Number((value & 0x07ff).toString(8)),
      polarity: value & 0x8000 ? 'R' : 'N',
    }
  }
  return {
    mode: 'ctcss',
    ctcss: (value & 0x7fff) / 10,
    dtcs: null,
    polarity: 'N',
  }
}

function encodeTone(channel: WebChannel): number {
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    return Math.round(channel.ctcss * 10)
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    let value = Number.parseInt(channel.dtcs.toString(), 8) | 0x2800
    if (channel.dtcsPolarity === 'R') value |= 0x8000
    return value
  }
  return 0
}

export function parseTKx102Channel(
  image: Uint8Array,
  profile: TKx102Profile,
  number: number,
): WebChannel {
  if (number < 1 || number > CHANNEL_COUNT) {
    throw new Error('TK-x102 channel numbers must be between 1 and 8.')
  }
  const start = CHANNEL_START + (number - 1) * CHANNEL_SIZE
  const raw = image.slice(start, start + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE || raw.slice(0, 4).every((value) => value === 0xff)) {
    return emptyChannel(number)
  }
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  const txFrequency = decodeLbcd(raw.slice(4, 8))
  if (!rxFrequency) return emptyChannel(number)
  const transmit = decodeTone(readUint16LE(raw, 10))
  const receive = decodeTone(readUint16LE(raw, 8))
  const selected = transmit.mode === 'none' ? receive : transmit
  return {
    number,
    empty: false,
    name: '',
    rxFrequency,
    txFrequency: txFrequency ?? rxFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: raw[12] & 0x02 ? 'FM' : 'NFM',
    power: raw[13] & 0x20 ? 'High' : 'Low',
    skip: (raw[13] & 0x10) === 0,
  }
}

export function parseTKx102Channels(
  image: Uint8Array,
  profile: TKx102Profile,
): WebChannel[] {
  return Array.from({ length: CHANNEL_COUNT }, (_, index) =>
    parseTKx102Channel(image, profile, index + 1),
  )
}

export function applyTKx102Channel(
  image: Uint8Array,
  profile: TKx102Profile,
  channel: WebChannel,
): void {
  if (channel.number < 1 || channel.number > CHANNEL_COUNT) {
    throw new Error('TK-x102 channel numbers must be between 1 and 8.')
  }
  const start = CHANNEL_START + (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(start, start + CHANNEL_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  raw.fill(0)
  raw[14] = 0x07
  raw[15] = 0x22
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  encodeLbcd(channel.txFrequency ?? channel.rxFrequency, raw.subarray(4, 8))
  const tone = encodeTone(channel)
  writeUint16LE(raw, 8, tone)
  writeUint16LE(raw, 10, tone)
  raw[12] = channel.mode === 'FM' ? 0x02 : 0
  raw[13] =
    (channel.power === 'High' ? 0x20 : 0) |
    (channel.skip ? 0 : 0x10)
}

function frame(command: number, address: number, length: number): Uint8Array {
  return Uint8Array.of(
    command,
    (address >>> 8) & 0xff,
    address & 0xff,
    length,
  )
}

async function readAck(transport: RadioTransport): Promise<void> {
  if ((await transport.readExact(1, 2500))[0] !== ACK) {
    throw new Error('The Kenwood radio rejected the operation.')
  }
}

async function enter(
  profile: TKx102Profile,
  transport: RadioTransport,
): Promise<void> {
  await transport.discardInput()
  await transport.write(new TextEncoder().encode('PROGRAM'))
  await readAck(transport)
  await transport.write(Uint8Array.of(0x02))
  const identity = await transport.readExact(8, 2500)
  const model = new TextDecoder().decode(identity.slice(1, 5)).trim()
  if (model !== profile.ident) {
    throw new Error(
      `The connected radio identifies as TK-${model || 'unknown'}, not ${profile.model}.`,
    )
  }
  await transport.write(Uint8Array.of(ACK))
  await readAck(transport)
}

export async function readTKx102RadioWithTransport(
  profile: TKx102Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enter(profile, transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE).fill(0xff)
    image.set(new TextEncoder().encode('EX3402'))
    const total = RADIO_MEMORY_SIZE / BLOCK_SIZE
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      await transport.write(frame(0x52, address, BLOCK_SIZE))
      const header = await transport.readExact(4, 3000)
      if (
        header[0] !== 0x52 ||
        ((header[1] << 8) | header[2]) !== address ||
        header[3] !== BLOCK_SIZE
      ) {
        throw new Error(`Unexpected TK-x102 reply at 0x${address.toString(16)}.`)
      }
      image.set(
        await transport.readExact(BLOCK_SIZE, 3000),
        FILE_HEADER_SIZE + address,
      )
      await transport.write(Uint8Array.of(ACK))
      await readAck(transport)
      onProgress?.({
        current: index + 1,
        total,
        label: 'Reading Kenwood TK-x102 memory',
      })
    }
    return { image, reportedModel: profile.model }
  } finally {
    await transport.write(Uint8Array.of(0x45))
  }
}

export async function writeTKx102RadioWithTransport(
  profile: TKx102Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than TK-x102 memory.')
  }
  await enter(profile, transport)
  try {
    const total = RADIO_MEMORY_SIZE / BLOCK_SIZE
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      const packet = new Uint8Array(12)
      packet.set(frame(0x57, address, BLOCK_SIZE))
      packet.set(
        image.slice(
          FILE_HEADER_SIZE + address,
          FILE_HEADER_SIZE + address + BLOCK_SIZE,
        ),
        4,
      )
      await transport.write(packet)
      await readAck(transport)
      await transport.write(Uint8Array.of(ACK))
      onProgress?.({
        current: index + 1,
        total,
        label: 'Writing Kenwood TK-x102 memory',
      })
    }
  } finally {
    await transport.write(Uint8Array.of(0x45))
  }
}
